import { Link } from "react-router-dom";
import { Twitter, Instagram, Facebook, Youtube } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  const socialLinks = [
    {
      name: "Twitter",
      icon: Twitter,
      href: "https://x.com/fanjagofootball",
    },
    {
      name: "Instagram",
      icon: Instagram,
      href: "https://www.instagram.com/fanjago_football",
    },
    {
      name: "Facebook",
      icon: Facebook,
      href: "https://www.facebook.com/61583617752484",
    },
    {
      name: "Youtube",
      icon: Youtube,
      href: "https://www.youtube.com/@Fanjago-Football",
    },
  ];

  return (
    <footer className="bg-background border-t border-border/50 py-16">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Logo and Tagline */}
          <div className="col-span-2 md:col-span-1">
            <div className="mb-3">
              <BrandLogo
                to="/"
                size="sm"
                className="text-foreground hover:text-accent-soft transition-colors"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {t("marketing.footer.tagline", {
                defaultValue: "All-in-one football management for everyone.",
              })}
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="font-heading font-semibold mb-4 text-foreground">
              {t("marketing.footer.product", { defaultValue: "Product" })}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-sm text-muted-foreground hover:text-accent-soft transition-colors"
                >
                  {t("marketing.footer.overview", { defaultValue: "Overview" })}
                </Link>
              </li>
              <li>
                <Link
                  to="/player"
                  className="text-sm text-muted-foreground hover:text-accent-soft transition-colors"
                >
                  {t("marketing.footer.player", { defaultValue: "Player" })}
                </Link>
              </li>
              <li>
                <Link
                  to="/coach"
                  className="text-sm text-muted-foreground hover:text-accent-soft transition-colors"
                >
                  {t("marketing.footer.coach", { defaultValue: "Coach" })}
                </Link>
              </li>
              <li>
                <Link
                  to="/club-owner"
                  className="text-sm text-muted-foreground hover:text-accent-soft transition-colors"
                >
                  {t("marketing.footer.clubOwner", { defaultValue: "Club Owner" })}
                </Link>
              </li>
              <li>
                <Link
                  to="/parent"
                  className="text-sm text-muted-foreground hover:text-accent-soft transition-colors"
                >
                  {t("marketing.footer.parent", { defaultValue: "Parent" })}
                </Link>
              </li>
              <li>
                <Link
                  to="/venue-owner"
                  className="text-sm text-muted-foreground hover:text-accent-soft transition-colors"
                >
                  {t("marketing.footer.venueOwner", { defaultValue: "Venue Owner" })}
                </Link>
              </li>
              <li>
                <Link
                  to="/tournament-organizer"
                  className="text-sm text-muted-foreground hover:text-accent-soft transition-colors"
                >
                  {t("marketing.footer.tournamentOrganizer", {
                    defaultValue: "Tournament Organizer",
                  })}
                </Link>
              </li>
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-heading font-semibold mb-4 text-foreground">
              {t("marketing.footer.company", { defaultValue: "Company" })}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/about"
                  className="text-sm text-muted-foreground hover:text-accent-soft transition-colors"
                >
                  {t("marketing.footer.about", { defaultValue: "About Us" })}
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-sm text-muted-foreground hover:text-accent-soft transition-colors"
                >
                  {t("marketing.footer.contact", { defaultValue: "Contact Us" })}
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  className="text-sm text-muted-foreground hover:text-accent-soft transition-colors"
                >
                  {t("marketing.footer.faq", { defaultValue: "FAQ" })}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-heading font-semibold mb-4 text-foreground">
              {t("marketing.footer.legal", { defaultValue: "Legal" })}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/terms"
                  className="text-sm text-muted-foreground hover:text-accent-soft transition-colors"
                >
                  {t("marketing.footer.terms", {
                    defaultValue: "Terms Of Service",
                  })}
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-sm text-muted-foreground hover:text-accent-soft transition-colors"
                >
                  {t("marketing.footer.privacy", {
                    defaultValue: "Privacy & Policy",
                  })}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-border/50 pt-8">
          <div className="flex flex-col items-center gap-6">
            {/* Social Media Icons */}
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-surface/50 border border-border/50 flex items-center justify-center text-muted-foreground hover:text-accent-soft hover:border-accent-soft/50 hover:bg-accent/10 transition-all duration-300"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
            {/* Copyright */}
            <p className="text-sm text-muted-foreground text-center">
              © FanJago {currentYear}. {t("marketing.footer.rights", {
                defaultValue: "All rights reserved.",
              })}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
